import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import PreviewIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Typography, List, ListItem, ListItemText, Tooltip, Button } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useConfig } from '../../contexts/ConfigContext';
import { useOptions } from '../../contexts/OptionsContext';
import { CultureMapAPI, MediaItemDetailed, MinimalDataItem, OptionsData } from '../../types';
import { apiFetch, REACT_APP_API_ROOT } from '../../utilities/api';
import { MultiSelectDialog } from '../common/MultiSelectDialog';
import { QuickNewItemDialog } from '../common/QuickNewItemDialog';
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import { useSpinner } from '../../contexts/SpinnerContext';
import MediaViewer from "../media/MediaViewer";
import AddIcon from '@mui/icons-material/Add';
import { needsServerConversion } from '../../utilities/mediaTypes';
import { handleFormSubmissionError } from '../../utilities/errorHandlers';

interface RelationshipData {
  id: number;
  type: string;
  description: string;
  is_primary: boolean;
}

interface UploadTableContainerProps {
  items: MediaItemDetailed[];
  onItemSelect: (id: string) => void;
  selectedItemId?: string;
  onPreviewClick: (item: {
    mediaUrl: string;
    mediaType?: string;
    thumbnail?: string;
  }) => void;
  onItemUpdate: (item: Partial<MediaItemDetailed>) => void;
  onBulkUpdate: (
    updates: Array<{ id: string; updates: Partial<MediaItemDetailed> }>
  ) => void;
  onFilesDropped: (files: File[]) => void;
  onDeleteItem: (id: string) => void;
  tableType?: string;
  mode?: 'upload' | 'edit';
}

// Add at the top with other constants
const BLOCKED_TABLES = ['family-areas', 'places'];
const MAX_PREVIEW_SIZE = 100 * 1024 * 1024; // 100MB max for creating blob URLs in previews

const UploadTableContainer: React.FC<UploadTableContainerProps> = ({
  items,
  onItemSelect,
  selectedItemId,
  onPreviewClick,
  onBulkUpdate,
  onItemUpdate,
  onFilesDropped,
  onDeleteItem,
  tableType = "media",
  mode = 'upload',
}) => {

  
  const config = useConfig();
  const { options, refreshOptions, isLoading } = useOptions();
  const [newItemDialog, setNewItemDialog] = useState<{
    open: boolean;
    category: string;
    endpoint: string;
    onSuccess: (createdItem?: { id: number; name: string }) => void;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [disabledDeleteButtons, setDisabledDeleteButtons] = useState<
    Set<string>
  >(new Set());
  const { startLoading, stopLoading } = useSpinner();
  const [previewItem, setPreviewItem] = useState<MediaItemDetailed | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [blobUrls, setBlobUrls] = useState<Map<string, string>>(new Map());

  // Add dropzone functionality
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onFilesDropped,
  });

  // Validate config has required fields
  const validateConfig = () => {
    if (!config) {
      throw new Error("Configuration is not loaded");
    }
    return true;
  };

  const handleAddNew = (category: string, onSuccess?: (newItemId: number) => void) => {
    const endpointConfig = config.apiEndpoints.find(
      (e) => e.name.toLowerCase() === category.toLowerCase()
    );

    if (!endpointConfig) {
      enqueueUniqueSnackbar(enqueueSnackbar, `Configuration error for ${category}`, { variant: 'error' });
      return;
    }

    // Block creation for specific tables
    if (BLOCKED_TABLES.includes(endpointConfig.tableName)) {
      enqueueUniqueSnackbar(enqueueSnackbar, `Cannot create new ${category} items`, { variant: 'warning' });
      return;
    }

    if (!selectedItemId) {
      enqueueUniqueSnackbar(enqueueSnackbar, 'No item selected to update', { variant: 'error' });
      return;
    }

    setNewItemDialog({
      open: true,
      category,
      endpoint: endpointConfig.api_endpoint,
      onSuccess: async (createdItem?: { id: number; name: string }) => {
        try {
          if (!createdItem?.id || !createdItem?.name) {
            throw new Error('Invalid created item data');
          }
          startLoading();
          
          await refreshOptions();
          await new Promise(resolve => setTimeout(resolve, 500));

          const currentItem = items.find(item => item.id === selectedItemId);
          if (!currentItem) {
            throw new Error('Selected item not found');
          }

          // Validate current relationships structure
          if (!currentItem.relationships) {
            currentItem.relationships = {
              related_entities: {},
              geo_entities: {},
              media: []
            };
          }

          // Create the update object based on entity type
          const update: Partial<MediaItemDetailed> = {
            id: currentItem.id,
            relationships: {
              ...currentItem.relationships,
              [endpointConfig.entityType === 'geo' ? 'geo_entities' : 
               endpointConfig.entityType === 'media' ? 'media' : 
               'related_entities']: endpointConfig.entityType === 'media' 
                ? [...(currentItem.relationships?.media || []), { 
                    id: createdItem.id,
                    entity_name: createdItem.name,
                    rel_description: "Associated at Upload",
                    is_primary: false
                  }]
                : {
                    ...currentItem.relationships?.[endpointConfig.entityType === 'geo' ? 'geo_entities' : 'related_entities'],
                    [endpointConfig.returnedRelationshipsKey]: [
                      ...(currentItem.relationships?.[endpointConfig.entityType === 'geo' ? 'geo_entities' : 'related_entities']?.[endpointConfig.returnedRelationshipsKey] || []),
                      {
                        id: createdItem.id,
                        entity_name: createdItem.name,
                        rel_description: "Associated at Upload",
                        is_primary: false
                      }
                    ]
                  }
            }
          };

          onItemUpdate(update);
          setNewItemDialog(null);
          enqueueSnackbar("Item created and added successfully", { variant: "success" });
        } catch (error) {
          enqueueUniqueSnackbar(
            enqueueSnackbar,
            error instanceof Error ? error.message : 'Error creating or adding item. Please try again.',
            { variant: 'error' }
          );
        } finally {
          stopLoading();
        }
      }
    });
  };

  const handleCreateNewItem = async (name: string, description: string) => {
    setError(null);
    if (!newItemDialog) return;

    try {
      if (!name.trim()) {
        enqueueSnackbar("Name is required", { variant: "error" });
        return;
      }
      if (!description || !description.trim()) {
        enqueueSnackbar("Description is required", { variant: "error" });
        return;
      }

      validateConfig();

      const endpointConfig = config.apiEndpoints.find(
        (e) => e.name.toLowerCase() === newItemDialog.category.toLowerCase()
      );

      if (!endpointConfig || !endpointConfig.api_endpoint) {
        throw new Error(`No endpoint configured for ${newItemDialog.category}`);
      }

      const payload = {
        name: name.trim(),
        description: description.trim(),
        primary_image_write: null
      };

      const response = await apiFetch(
        `${REACT_APP_API_ROOT}${endpointConfig.api_endpoint}`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      await newItemDialog.onSuccess(response);
      setNewItemDialog(null);
    } catch (error) {
      handleFormSubmissionError(error, enqueueSnackbar, {
        action: 'create',
        itemType: 'item'
      });
      throw error;
    }
  };

  // All hooks must be at the top level
  useEffect(() => {
    try {
      validateConfig();
      console.log("Options keys:", Object.keys(options || {}));

      // Test generating a single column for Country Areas
      const countryAreaEndpoint = config.apiEndpoints.find(
        (e) => e.tableName === "family-areas"
      );

      if (countryAreaEndpoint) {
        const generatedColumn = generateRelationshipColumn(
          countryAreaEndpoint,
          options,
          handleAddNew,
          renderHeaderWithFillDown
        );
        console.log("Generated Country Areas column:", generatedColumn);

        // Compare with existing column
        const existingColumn = columns.find(
          (col) => col.field === "countryAreaIds"
        );
        console.log("Existing Country Areas column:", existingColumn);
      } else {
        console.warn("Could not find family-areas endpoint in config");
      }
    } catch (error) {
      console.error("Configuration validation failed:", error);
      setError(
        error instanceof Error ? error.message : "Invalid configuration"
      );
    }
  }, [config, options]);

  // Add safety check for selectedItemId
  useEffect(() => {
    if (selectedItemId && !items.some(item => item.id === selectedItemId)) {
      // If selected item no longer exists, clear selection
      onItemSelect?.('');
    }
  }, [items, selectedItemId, onItemSelect]);

  // Cleanup blob URLs when component unmounts or items change
  useEffect(() => {
    return () => {
      // Cleanup all blob URLs on unmount
      blobUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Clean up blob URLs for items that no longer exist
  useEffect(() => {
    const currentItemIds = new Set(items.map(item => String(item.id)));
    const urlsToCleanup: string[] = [];
    
    blobUrls.forEach((url, itemId) => {
      if (!currentItemIds.has(itemId)) {
        URL.revokeObjectURL(url);
        urlsToCleanup.push(itemId);
      }
    });
    
    if (urlsToCleanup.length > 0) {
      setBlobUrls(prev => {
        const newMap = new Map(prev);
        urlsToCleanup.forEach(id => newMap.delete(id));
        return newMap;
      });
    }
  }, [items, blobUrls]);

  // Show error state in the UI if needed
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const renderHeaderWithFillDown = (label: string, field: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {selectedItemId && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleFillDown(field);
          }}
          sx={{
            padding: 0.5,
            zIndex: 10,
            position: 'relative',
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
          }}
        >
          <KeyboardDoubleArrowDownIcon fontSize="small" />
        </IconButton>
      )}
      {label}
    </Box>
  );

  const handleFillDown = async (field: string) => {
    if (!selectedItemId || !onBulkUpdate) return;

    const selectedIndex = items.findIndex((item) => item.id === selectedItemId);
    if (selectedIndex === -1) {
      console.warn('Selected item no longer exists in table');
      onItemSelect?.('');
      return;
    }

    const selectedItem = items[selectedIndex] as MediaItemDetailed;
    
    // Handle basic fields (name and description)
    if (field === "name" || field === "description") {
      const updates = items.slice(selectedIndex + 1).map((item) => ({
        id: String(item.id),
        updates: {
          [field]: selectedItem[field]
        }
      }));
      onBulkUpdate(updates);
      return;
    }

    // Handle relationship fields (existing code)
    const endpoint = config.apiEndpoints.find(ep => ep.tableName === field);
    if (!endpoint) return;

    // Create updates for all rows below the selected one
    const updates = items.slice(selectedIndex + 1).map((item) => ({
      id: String(item.id),
      updates: {
        relationships: {
          ...item.relationships,
          [endpoint.entityType === 'geo' ? 'geo_entities' : 
           endpoint.entityType === 'media' ? 'media' : 
           'related_entities']: endpoint.entityType === 'media' 
            ? [...(selectedItem.relationships?.media || [])]
            : {
                ...item.relationships?.[endpoint.entityType === 'geo' ? 'geo_entities' : 'related_entities'],
                [endpoint.returnedRelationshipsKey]: [...(selectedItem.relationships?.[endpoint.entityType === 'geo' ? 'geo_entities' : 'related_entities']?.[endpoint.returnedRelationshipsKey] || [])]
              }
        }
      }
    }));

    onBulkUpdate(updates);
  };

  const handleProcessRowUpdate = (newRow: any, oldRow: MediaItemDetailed) => {
    // Process updates to table rows
    try {
      if (!items.some(item => item.id === newRow.id)) {
        return oldRow;
      }

      // Find which field changed
      const changedField = Object.keys(newRow).find(
        key => JSON.stringify(newRow[key]) !== JSON.stringify(oldRow[key])
      );

      if (!changedField) return oldRow;

      // Handle basic field updates
      if (changedField === 'name' || changedField === 'description') {
        const update: Partial<MediaItemDetailed> = {
          id: newRow.id,
          [changedField]: newRow[changedField]
        };
        onItemUpdate?.(update);
        return { ...oldRow, ...update };
      }

      // Find matching endpoint for relationship updates
      const endpoint = config.apiEndpoints.find(
        ep => ep.tableName === changedField
      );

      if (!endpoint) return oldRow;

      // Format selected items for relationship update
      const selectedItems = newRow[changedField].map((id: number) => {
        const optionItem = options?.[endpoint.tableName]?.find(opt => opt.id === id);
        return {
          id,
          entity_name: optionItem?.name,
          rel_description: "Associated at Upload",
          is_primary: false
        };
      });

      // Create relationship update object
      const update: Partial<MediaItemDetailed> = {
        id: newRow.id,
        relationships: {
          ...oldRow.relationships,
          [endpoint.entityType === 'geo' ? 'geo_entities' : 
           endpoint.entityType === 'media' ? 'media' : 
           'related_entities']: endpoint.entityType === 'media' 
            ? selectedItems
            : {
                ...oldRow.relationships?.[endpoint.entityType === 'geo' ? 'geo_entities' : 'related_entities'],
                [endpoint.returnedRelationshipsKey]: selectedItems
              }
        }
      };

      onItemUpdate?.(update);
      return { ...oldRow, ...update };
    } catch (error) {
      return oldRow;
    }
  };

  const generateRelationshipColumn = (
    endpoint: (typeof config.apiEndpoints)[0],
    options: OptionsData | null,
    onAddNew: typeof handleAddNew,
    renderHeader: (label: string, field: string) => React.ReactNode
  ): GridColDef => {
    const fieldName = endpoint.tableName;

    return {
      field: fieldName,
      headerName: endpoint.name,
      renderHeader: () => renderHeader(endpoint.name, fieldName),
      flex: 1,
      editable: true,
      sortable: false,
      valueGetter: (params: GridRenderCellParams<MediaItemDetailed>) => {
        try {
          if (!params?.row?.relationships) return [];
          
          // Add logging to track relationship data
          console.debug(`ValueGetter for ${endpoint.name}:`, {
            entityType: endpoint.entityType,
            relationships: params.row.relationships,
            returnedKey: endpoint.returnedRelationshipsKey
          });
          
          // Use entityType from config to determine location in relationships
          switch (endpoint.entityType) {
            case 'geo':
              return params.row.relationships.geo_entities?.[endpoint.returnedRelationshipsKey]?.map(item => item.id) || [];
            case 'media':
              return params.row.relationships.media?.map(item => item.id) || [];
            default: // 'related' or undefined
              return params.row.relationships.related_entities?.[endpoint.returnedRelationshipsKey]?.map(item => item.id) || [];
          }
        } catch (error) {
          console.error(`Error getting value for ${endpoint.name}:`, error);
          return [];
        }
      },
      renderCell: (params: GridRenderCellParams<MediaItemDetailed>) => {
        try {
          if (!params?.row?.relationships) return "";
          
          // // Add logging to track options state
          // console.debug(`RenderCell for ${endpoint.name}:`, {
          //   hasOptions: Boolean(options?.[endpoint.tableName]),
          //   optionsCount: options?.[endpoint.tableName]?.length
          // });
          
          // Use entityType to get items from correct location
          let relationshipItems;
          switch (endpoint.entityType) {
            case 'geo':
              relationshipItems = params.row.relationships.geo_entities?.[endpoint.returnedRelationshipsKey] || [];
              break;
            case 'media':
              relationshipItems = params.row.relationships.media || [];
              break;
            default: // 'related' or undefined
              relationshipItems = params.row.relationships.related_entities?.[endpoint.returnedRelationshipsKey] || [];
          }
          
          // If no items, just return empty string
          if (relationshipItems.length === 0) return "";
          
          // Find option items for display with fallback
          const itemsWithNames = relationshipItems.map(item => {
            const optionItem = options?.[endpoint.tableName]?.find(opt => opt.id === item.id);
            const name = optionItem?.name || item.entity_name || `ID: ${item.id}`;
            return { ...item, displayName: name };
          });
          
          // Create tooltip content - more detailed view of the selected items
          const tooltipContent = (
            <Box sx={{ p: 1, maxWidth: 300 }}>
              <Typography variant="subtitle2">{endpoint.name} ({itemsWithNames.length})</Typography>
              <List dense disablePadding>
                {itemsWithNames.map(item => (
                  <ListItem key={item.id} dense disablePadding sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary={item.displayName}
                      secondary={item.rel_description ? item.rel_description : null}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
          
          // Return cell with tooltip
          return (
            <Tooltip
              title={tooltipContent}
              arrow
              placement="top-start"
              componentsProps={{
                tooltip: {
                  sx: { bgcolor: 'background.paper', color: 'text.primary', boxShadow: 2, borderRadius: 1 }
                }
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap', 
                  width: '100%',
                  fontSize: '0.75rem',
                  display: 'block'
                }}
              >
                {itemsWithNames.map(item => item.displayName).join(", ")}
              </Typography>
            </Tooltip>
          );
        } catch (error) {
          console.error('Error rendering cell:', error);
          return <Typography color="error">Error</Typography>;
        }
      },
      renderEditCell: (params) => {
        try {
          return (
            <MultiSelectDialog
              params={params}
              options={options?.[endpoint.tableName] || []}
              onAddNew={() => onAddNew(endpoint.name)}
              category={endpoint.name}
              config={config}
            />
          );
        } catch (error) {
          console.error("Error rendering edit cell:", error);
          return null;
        }
      },
      cellClassName: (params) => {
        // Add error styling if this item failed to upload
        if (params.row.uploadError) {
          return 'error-cell';
        }
        return '';
      },
    };
  };

  // Add utility function to identify server-side items
  const isServerItem = (item: MediaItemDetailed): boolean => {
    return Boolean(item.url && !item.file);
  };

  // First, separate the Preview column from other hardcoded columns
  const baseColumns: GridColDef[] = [
    { field: "type", headerName: "Type", width: 70, sortable: false },
    { 
      field: "name", 
      headerName: "Name", 
      flex: 1, 
      editable: true, 
      sortable: false,
      renderHeader: () => renderHeaderWithFillDown("Name", "name"),
      renderCell: (params: GridRenderCellParams<MediaItemDetailed>) => {
        const value = params.value?.toString() || '';
        
        // Create tooltip content with the same styling as relationship tooltips
        const tooltipContent = (
          <Box sx={{ p: 1, maxWidth: 300 }}>
            <Typography variant="body2">{value}</Typography>
          </Box>
        );
        
        return (
          <Tooltip
            title={tooltipContent}
            arrow
            placement="top-start"
            componentsProps={{
              tooltip: {
                sx: { bgcolor: 'background.paper', color: 'text.primary', boxShadow: 2, borderRadius: 1 }
              }
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap', 
                width: '100%',
                fontSize: '0.75rem',
                display: 'block'
              }}
            >
              {value}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      editable: true,
      sortable: false,
      renderHeader: () => renderHeaderWithFillDown("Description", "description"),
      renderCell: (params: GridRenderCellParams<MediaItemDetailed>) => {
        const value = params.value?.toString() || '';
        
        // Create tooltip content with the same styling as relationship tooltips
        const tooltipContent = (
          <Box sx={{ p: 1, maxWidth: 300 }}>
            <Typography variant="body2">{value}</Typography>
          </Box>
        );
        
        return (
          <Tooltip
            title={tooltipContent}
            arrow
            placement="top-start"
            componentsProps={{
              tooltip: {
                sx: { bgcolor: 'background.paper', color: 'text.primary', boxShadow: 2, borderRadius: 1 }
              }
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap', 
                width: '100%',
                fontSize: '0.75rem',
                display: 'block'
              }}
            >
              {value}
            </Typography>
          </Tooltip>
        );
      }
    },
  ];

  const previewColumn: GridColDef = {
    field: "preview",
    headerName: "Preview",
    width: 70,
    sortable: false,
    renderCell: (params) => {
      const item = params.row as MediaItemDetailed;
      
      // For files being uploaded (local files)
      if (item.file instanceof File) {
        // Check if file needs server-side conversion (MTS, VOB, MKV, etc.)
        if (needsServerConversion(item.file)) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                opacity: 0.7,
                '&:hover': { opacity: 1 }
              }}
              onClick={(e) => {
                e.stopPropagation();
                setPreviewItem(item);
                setIsPreviewOpen(true);
              }}
            >
              <img
                src="/icons/video-thumbnail.svg"
                alt="Video needs conversion"
                style={{ width: 24, height: 24 }}
                title="This video format will be converted on the server for playback"
              />
            </Box>
          );
        }

        // Check file size to prevent memory issues with large files
        if (item.file.size > MAX_PREVIEW_SIZE) {
          // For large files, show a static icon instead of creating blob URLs
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                opacity: 0.7,
                '&:hover': { opacity: 1 }
              }}
              onClick={(e) => {
                e.stopPropagation();
                setPreviewItem(item);
                setIsPreviewOpen(true);
              }}
            >
              <img
                src="/icons/video-thumbnail.svg"
                alt="Large file preview"
                style={{ width: 24, height: 24 }}
              />
            </Box>
          );
        }

        // Get or create managed blob URL
        const getManagedBlobUrl = (itemId: string, file: File): string => {
          const existingUrl = blobUrls.get(itemId);
          if (existingUrl) {
            return existingUrl;
          }
          
          const newUrl = URL.createObjectURL(file);
          setBlobUrls(prev => new Map(prev.set(itemId, newUrl)));
          return newUrl;
        };

        const managedUrl = getManagedBlobUrl(String(item.id), item.file);

        return (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setPreviewUrl(managedUrl);
              setPreviewItem(item);
              setIsPreviewOpen(true);
            }}
            size="small"
          >
            <PreviewIcon />
          </IconButton>
        );
      }
      // For server-side media items
      else if (item.url) {
        return (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setPreviewItem(item);
              setIsPreviewOpen(true);
              onPreviewClick({
                mediaUrl: item.url ?? '',
                mediaType: item.media_type ?? 'unknown',
                thumbnail: item.thumbnail ?? ''
              });
            }}
            size="small"
          >
            <PreviewIcon />
          </IconButton>
        );
      }
      return null;
    },
  };

  const handleDelete = async (id: string) => {
    // Prevent multiple rapid clicks
    if (disabledDeleteButtons.has(id)) {
      return;
    }

    // Disable the delete button immediately
    setDisabledDeleteButtons((prev) => new Set(prev).add(id));

    try {
      // Clean up any managed blob URLs for the item being deleted
      const existingUrl = blobUrls.get(id);
      if (existingUrl) {
        URL.revokeObjectURL(existingUrl);
        setBlobUrls(prev => {
          const newMap = new Map(prev);
          newMap.delete(id);
          return newMap;
        });
      }

      // If this is the selected row, clear selection first
      if (id === selectedItemId) {
        onItemSelect('');
      }

      // Clear preview if showing deleted item
      if (previewItem?.id === id) {
        setPreviewItem(null);
        setIsPreviewOpen(false);
      }

      // Small delay to allow state updates to propagate
      await new Promise(resolve => setTimeout(resolve, 50));

      // In edit mode, we might want to confirm deletion of server-side items
      if (mode === 'edit') {
        const item = items.find(item => item.id === id);
        // Check if this is a server-side item (has a URL but no file)
        if (item?.url && !item.file) {
          // In a real implementation, we might show a confirmation dialog
          const confirmDelete = window.confirm(
            `Are you sure you want to delete "${item.name || 'this item'}"? This action cannot be undone.`
          );
          
          if (!confirmDelete) {
            // Re-enable the button if user cancels
            setDisabledDeleteButtons((prev) => {
              const newSet = new Set(prev);
              newSet.delete(id);
              return newSet;
            });
            return;
          }
        }
      }

      // Call the delete handler
      onDeleteItem(id);
    } finally {
      // Re-enable the button after a short delay
      setTimeout(() => {
        setDisabledDeleteButtons((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }, 100);
    }
  };

  const deleteColumn: GridColDef = {
    field: "delete",
    headerName: "",
    width: 35,
    sortable: false,
    renderCell: (params) => (
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleDelete(params.row.id);
        }}
        size="small"
        disabled={disabledDeleteButtons.has(params.row.id)}
        sx={{ padding: 0.5 }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    ),
  };

  // Then combine them in the right order, ensuring unique field names
  const columns = [
    ...baseColumns,
    ...config.apiEndpoints
      .filter((endpoint) => endpoint.tableName !== tableType)
      .map((endpoint) => {
        return generateRelationshipColumn(
          endpoint,
          options,
          handleAddNew,
          renderHeaderWithFillDown
        );
      })
      .filter((column): column is GridColDef => column !== null),
    deleteColumn,
  ];

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = Array.from(event.dataTransfer.files);
    onFilesDropped(files);
  };

  // Add this method to format the relationships for POST
  const formatItemForUpload = (item: MediaItemDetailed) => {
    const formData = new FormData();
    formData.append("name", item.name);
    formData.append("description", item.description);
    formData.append("type", item.type);

    // Format relationships using config
    const relationships: CultureMapAPI.RelationshipInput[] = [];

    // Add relationships from geo_entities
    if (item.relationships?.geo_entities) {
      Object.entries(item.relationships.geo_entities).forEach(([key, items]) => {
        const endpoint = config.apiEndpoints.find(e => e.tableName === key);
        if (endpoint?.relationshipKey) {
          items.forEach(item => {
            relationships.push({
              id: Number(item.id),
              type: endpoint.relationshipKey,
              rel_description: "Associated at Upload",
              is_primary: false,
            });
          });
        }
      });
    }

    // Add relationships from related_entities
    if (item.relationships?.related_entities) {
      Object.entries(item.relationships.related_entities).forEach(([key, items]) => {
        const endpoint = config.apiEndpoints.find(e => e.tableName === key);
        if (endpoint?.relationshipKey) {
          items.forEach(item => {
            relationships.push({
              id: Number(item.id),
              type: endpoint.relationshipKey,
              rel_description: "Associated at Upload",
              is_primary: false,
            });
          });
        }
      });
    }

    // Add relationships from media
    if (item.relationships?.media) {
      const mediaEndpoint = config.apiEndpoints.find(e => e.tableName === 'media');
      if (mediaEndpoint?.relationshipKey) {
        item.relationships.media.forEach(item => {
          relationships.push({
            id: Number(item.id),
            type: mediaEndpoint.relationshipKey,
            rel_description: "Associated at Upload",
            is_primary: false,
          });
        });
      }
    }

    formData.append("relationships", JSON.stringify(relationships));

    if (item.file) {
      formData.append("file", item.file);
    }

    if (item.original_file) {
      formData.append("original_file", JSON.stringify(item.original_file));
    }

    return formData;
  };

  const handleUploadAll = async () => {
    try {
      const formattedItems = items.map(formatItemForUpload);
      // ... rest of upload logic ...
    } catch (error) {
      console.error("Error formatting items for upload:", error);
      enqueueSnackbar("Error preparing items for upload", { variant: "error" });
    }
  };

  const handleClosePreview = () => {
    // Only revoke URL if it's not one of our managed blob URLs
    if (previewUrl && !blobUrls.has(previewItem?.id ? String(previewItem.id) : '')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewItem(null);
    setIsPreviewOpen(false);
  };

  const handleAddMoreClick = () => {
    // Trigger the hidden file input
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const filesArray = Array.from(event.target.files);
      onFilesDropped(filesArray);
      
      // Clear the input so the same file can be selected again if needed
      event.target.value = '';
    }
  };

  return (
    <>
      <Box 
        {...getRootProps()}
        sx={{ 
          height: '100%', 
          width: '100%', 
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden' // Prevent double scrollbars
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hidden file input for "ADD MORE" button */}
        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />

        {/* Empty state overlay - only show when there are no items */}
        {items.length === 0 && mode === 'upload' && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              cursor: 'pointer',
              backgroundColor: 'background.paper',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <img src="icons/upload.png" alt="Upload" style={{ width: 48, height: 48 }} />
            <Typography variant="body1" color="text.secondary">
              {isDragActive 
                ? "Drop files here ..."
                : "Click to select files or drop them here"
              }
            </Typography>
          </Box>
        )}

        {/* Empty state for edit mode */}
        {items.length === 0 && mode === 'edit' && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              backgroundColor: 'background.paper',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No items to edit. Select the items you want to edit in the Tables tab, then return to the Editor.
            </Typography>
          </Box>
        )}

        {/* DataGrid with updated styling */}
        <DataGrid
          rows={items}
          columns={columns}
          onRowClick={(params) => {
            if (items.some(item => item.id === params.row.id)) {
              onItemSelect?.(params.row.id);
            }
          }}
          processRowUpdate={handleProcessRowUpdate}
          onProcessRowUpdateError={(error) => {
            console.error('Error updating row:', error);
            enqueueSnackbar('Failed to update row', { variant: 'error' });
          }}
          getRowClassName={(params) =>
            `${params.row.id === selectedItemId ? "selected-row" : ""} ${
              params.row.uploadError ? "error-row" : ""
            }`
          }
          key={items.length}
          getRowId={(row) => {
            // console.log('Getting row ID:', row.id, typeof row.id);
            if (!row?.id) {
              console.warn('Row missing ID:', row);
              return Math.random().toString();
            }
            return row.id;
          }}
          sx={{
            flex: 1, // Take up remaining space
            width: '100%',
            '& .selected-row': {
              bgcolor: 'action.selected',
            },
            '& .error-row': {
              bgcolor: 'error.light',
              '&:hover': {
                bgcolor: 'error.main',
              },
            },
            '& .error-cell': {
              color: 'error.main',
            },
            border: isDragActive ? '2px dashed #2196f3' : 'none',
            // Ensure proper scrolling
            '& .MuiDataGrid-main': {
              overflow: 'hidden',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto',
            },
            // Optional: Hide scrollbar when not needed
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
          loading={isLoading}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography color="text.secondary">
                  {mode === 'upload' 
                    ? (isDragActive ? "Drop files here..." : "No files selected")
                    : "No items to edit. Select items in the Tables tab first."
                  }
                </Typography>
              </Box>
            ),
          }}
          // Add auto-height prop to ensure proper sizing
          autoHeight={false}
        />

        {/* Add a drop zone overlay when dragging - ALWAYS SHOW THIS WHEN DRAGGING REGARDLESS OF ITEM COUNT */}
        {isDragActive && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              border: '3px dashed #2196f3',
              borderRadius: '4px',
            }}
          >
            <Typography variant="h6" color="primary">
              Drop files here to {mode === 'upload' ? 'upload' : 'add to selection'}
            </Typography>
          </Box>
        )}
        
        {/* "Add More" Button - Now positioned at the bottom left */}
        {items.length > 0 && mode === 'upload' && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 180, // Increase spacing to prevent overlap
              zIndex: 3,
              display: 'flex',
              gap: 2
            }}
          >
            <Button
              variant="outlined" // Restore to outlined style
              color="primary"
              size="small" // Restore to small size
              startIcon={<AddIcon />}
              onClick={handleAddMoreClick}
              sx={{ minWidth: '120px' }} // Keep consistent width
            >
              Add More
            </Button>
          </Box>
        )}
      </Box>

      {/* QuickNewItemDialog */}
      {newItemDialog && (
        <QuickNewItemDialog
          open={newItemDialog.open}
          onClose={() => setNewItemDialog(null)}
          onSubmit={handleCreateNewItem}
          title={`Add New ${newItemDialog.category}`}
          category={newItemDialog.category}
          error={error}
        />
      )}

      <MediaViewer
        item={previewItem}
        url={previewUrl || undefined}
        open={isPreviewOpen}
        onClose={handleClosePreview}
        mode="dialog"
      />
    </>
  );
};

export default UploadTableContainer;
